/**
 * Checklist Completion Record Schema
 *
 * Defines the structure for all checklist completion records.
 * Designed for SMS audit compliance.
 */

export const CHECKLIST_TYPES = {
  DAILY_CHECK: 'daily_check',
  PERMIT_TO_WORK: 'permit_to_work',
  PERIODIC_INSPECTION: 'periodic_inspection',
  OPERATIONAL: 'operational',
  COMPANY: 'company'
};

export const PERMIT_TYPES = {
  HOT_WORK: 'hot_work',
  ENCLOSED_SPACE: 'enclosed_space',
  WORKING_ALOFT: 'working_aloft',
  WORKING_OVER_SIDE: 'working_over_side',
  ELECTRICAL: 'electrical_isolation'
};

/**
 * Context types for linking checklists to operational activities
 */
export const CONTEXT_TYPES = {
  VOYAGE: 'voyage',
  MAINTENANCE_JOB: 'maintenance_job',
  DEFECT: 'defect',
  EQUIPMENT: 'equipment',
  INSPECTION: 'inspection',
  DRILL: 'drill',
  RISK_ASSESSMENT: 'risk_assessment',
  PERMIT: 'permit'
};

/**
 * Create a new completion record
 * All records are immutable after creation
 */
export const createCompletionRecord = ({
  vesselId,
  vesselName,
  checklistType,
  checklistId,
  checklistName,
  completedBy,
  location = null,
  checks = [],
  signature = null,
  authorisedBy = null,
  authoriserSignature = null,
  notes = '',
  permitSpecificData = null,
  expiresAt = null,
  linkedContext = null,
  // New fields for operational checklists
  participants = [],           // Crew who participated (for drills)
  weatherConditions = null,    // Weather at time of completion (MOB drills)
  scenario = '',               // Drill scenario description
  retentionUntil = null,       // Record retention date (SI 1999/2722)
  autoLogToLogbook = true      // Auto-log completion to logbook
}) => {
  const now = new Date().toISOString();

  return {
    id: `CHK-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    vesselId,
    vesselName,
    checklistType,
    checklistId,
    checklistName,
    completedAt: now,
    completedBy: {
      id: completedBy.id || 'unknown',
      name: completedBy.name || 'Unknown User',
      role: completedBy.role || 'Crew'
    },
    location,
    checks: checks.map(check => ({
      id: check.id,
      text: check.text,
      passed: check.passed,
      notes: check.notes || null,
      timestamp: now
    })),
    signature,
    authorisedBy: authorisedBy ? {
      id: authorisedBy.id,
      name: authorisedBy.name,
      role: authorisedBy.role
    } : null,
    authoriserSignature,
    notes,
    permitSpecificData,
    expiresAt,
    defectsRaised: [],
    amendment: null,

    // Context linking - connects this checklist to related activities
    linkedContext: linkedContext ? {
      type: linkedContext.type,
      id: linkedContext.id,
      name: linkedContext.name || null,
      autoLinked: linkedContext.autoLinked !== false,
      autoLogToLogbook: autoLogToLogbook !== false
    } : null,

    // Drill/operational specific fields
    participants: participants.map(p => ({
      crewId: p.crewId || p.id,
      name: p.name,
      role: p.role || ''
    })),
    weatherConditions: weatherConditions ? {
      windSpeed: weatherConditions.windSpeed || '',
      seaState: weatherConditions.seaState || '',
      visibility: weatherConditions.visibility || '',
      notes: weatherConditions.notes || ''
    } : null,
    scenario: scenario || '',
    retentionUntil: retentionUntil || null,

    // Audit metadata
    _meta: {
      version: 1,
      createdAt: now,
      locked: true
    }
  };
};

/**
 * Create an amendment to an existing record
 */
export const createAmendment = (originalRecordId, reason, amendedBy) => {
  return {
    originalRecordId,
    reason,
    amendedAt: new Date().toISOString(),
    amendedBy: {
      id: amendedBy.id,
      name: amendedBy.name,
      role: amendedBy.role
    }
  };
};

/**
 * Validate a completion record before saving
 */
export const validateCompletionRecord = (record) => {
  const errors = [];

  if (!record.vesselId) errors.push('Vessel ID is required');
  if (!record.checklistType) errors.push('Checklist type is required');
  if (!record.completedBy?.name) errors.push('Completed by name is required');
  if (!record.checks || record.checks.length === 0) errors.push('At least one check is required');

  // Permit-specific validation
  if (record.checklistType === CHECKLIST_TYPES.PERMIT_TO_WORK) {
    if (!record.location) errors.push('Location is required for permits');
    if (!record.signature) errors.push('Signature is required for permits');

    // Check for high-risk permits requiring Master authorisation
    const highRiskPermits = [PERMIT_TYPES.ENCLOSED_SPACE, PERMIT_TYPES.HOT_WORK];
    if (highRiskPermits.includes(record.checklistId) && !record.authoriserSignature) {
      errors.push('Master authorisation signature required for this permit type');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};
